import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { Card, Kpi, SectionTitle, Segmented } from "../ui";
import { InteractiveWeeks, WeekHeatmap, DonutTooltip, FilterChip, ActivityTable } from "../charts";
import { Cat, Period, day, Granularity, bucketOf, catsByActivityCount } from "@/lib/dash";
import { Activity, DashboardData } from "@/lib/types";
import { fmtNum, fmtDate, CAT_COLORS } from "@/lib/format";
import { useUI } from "@/lib/theme";

// Métricas del chart de seguimiento (apilables por categoría) + el heatmap diario.
type Mode = "sesiones" | "km" | "horas" | "calorias" | "carga" | "heatmap";

// Valor que aporta un acto a la métrica seleccionada (el heatmap se gestiona aparte).
function metricVal(a: Activity, m: Mode): number {
  switch (m) {
    case "km":
      return a.distance_km;
    case "horas":
      return a.duration_min / 60;
    case "calorias":
      return a.total_calories ?? 0;
    case "carga":
      return a.relative_effort ?? 0;
    default:
      return 1; // sesiones
  }
}

// Decimales del eje/tooltip según métrica: km y horas con 1; el resto enteros.
const metricDecimals = (m: Mode): number => (m === "km" || m === "horas" ? 1 : 0);

export default function Resumen({
  acts,
  weekly,
  gearMap,
  granularity,
}: {
  acts: Activity[];
  weekly: DashboardData["weekly"];
  gearMap: Record<string, string>;
  granularity: Granularity;
}) {
  const { t, loc, palette } = useUI();

  // Vista del chart de seguimiento: sesiones (por defecto), km, horas, calorías, carga o heatmap diario.
  const [mode, setMode] = useState<Mode>("sesiones");
  const presentCats = useMemo(() => catsByActivityCount(acts), [acts]);

  // Eje del chart: en semanal usamos el agregado del backend (`weekly`); en diario
  // (rangos cortos) lo calculamos desde las actividades, bucket por día.
  const weeks = useMemo(
    () =>
      granularity === "day"
        ? Array.from(new Set(acts.map((a) => bucketOf(a.start_local, "day")))).sort()
        : Array.from(new Set(weekly.map((w) => w.week))).sort(),
    [granularity, acts, weekly],
  );
  // Apilado por categoría calculado desde `acts` (misma fuente que heatmap, KPIs, donut y
  // tabla), bucket por día o por semana. El agregado backend `weekly` no trae calorías, así
  // que computar aquí mantiene todas las métricas consistentes con el resto de la tab.
  const stacked = useMemo(() => {
    const decimals = metricDecimals(mode);
    const idx: Record<string, Record<string, number>> = {};
    for (const wk of weeks) idx[wk] = {};
    for (const a of acts) {
      const bucket = idx[bucketOf(a.start_local, granularity)];
      if (!bucket) continue;
      bucket[a.categoria] = (bucket[a.categoria] ?? 0) + metricVal(a, mode);
    }
    return weeks.map((wk) => {
      const row: Record<string, number | string> = { week: wk };
      for (const c of presentCats) {
        const v = idx[wk]?.[c] ?? 0;
        row[c] = decimals ? Number(v.toFixed(decimals)) : Math.round(v);
      }
      return row;
    });
  }, [granularity, weeks, acts, mode, presentCats]);

  const [period, setPeriod] = useState<Period | null>(null);
  const [selectedCat, setSelectedCat] = useState<Cat | null>(null);

  // El filtro de categoría se limpia al cambiar de rango.
  useEffect(() => setSelectedCat(null), [weekly]);
  // Al cambiar de vista (barras ↔ heatmap) limpiamos la selección para no arrastrarla.
  useEffect(() => setPeriod(null), [mode]);

  // Heatmap: clic en un día → filtra a ese día (o lo limpia).
  const onDay = (iso: string | null) => {
    if (!iso) {
      setPeriod(null);
      return;
    }
    const start = day(iso);
    const end = day(iso);
    end.setDate(end.getDate() + 1);
    setPeriod({ start, end, label: fmtDate(iso, loc) });
  };

  const periodActs = useMemo(() => {
    let r = acts;
    if (period)
      r = r.filter((a) => {
        const d = new Date(a.start_local);
        return d >= period.start && d < period.end;
      });
    if (selectedCat) r = r.filter((a) => a.categoria === selectedCat);
    return r;
  }, [acts, period, selectedCat]);

  const km = periodActs.reduce((s, a) => s + a.distance_km, 0);
  const horas = periodActs.reduce((s, a) => s + a.duration_min, 0) / 60;
  const kcal = periodActs.reduce((s, a) => s + (a.total_calories ?? 0), 0);

  // El donut usa el periodo (no la categoría) para no anularse a sí mismo.
  const periodOnly = useMemo(
    () =>
      period
        ? acts.filter((a) => {
            const d = new Date(a.start_local);
            return d >= period.start && d < period.end;
          })
        : acts,
    [acts, period],
  );
  const pie = presentCats.map((c) => {
    const arr = periodOnly.filter((a) => a.categoria === c);
    return {
      cat: c,
      name: t("cat_" + c),
      value: arr.length,
      km: arr.reduce((s, a) => s + a.distance_km, 0),
      hours: arr.reduce((s, a) => s + a.duration_min, 0) / 60,
      kcal: arr.reduce((s, a) => s + (a.total_calories ?? 0), 0),
    };
  }).filter((d) => d.value > 0);

  const tableActs = useMemo(
    () => [...periodActs].sort((a, b) => b.start_local.localeCompare(a.start_local)),
    [periodActs],
  );

  const filterLabel = [period?.label, selectedCat ? t("cat_" + selectedCat) : null]
    .filter(Boolean)
    .join(" · ");
  const anyFilter = !!period || !!selectedCat;

  return (
    <div className="space-y-6">
      {/* Chip del filtro activo, arriba del todo (coherente con Salud): deja claro que afecta a KPIs, charts y tabla. */}
      {anyFilter && (
        <div className="flex">
          <FilterChip
            label={filterLabel}
            onClear={() => {
              setPeriod(null);
              setSelectedCat(null);
            }}
            color={selectedCat ? CAT_COLORS[selectedCat] : "#FC4C02"}
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Kpi label={t("kpi_actividades")} value={fmtNum(periodActs.length, 0, loc)} accent />
        <Kpi label={t("kpi_kilometros")} value={fmtNum(km, 0, loc)} accent />
        <Kpi label={t("kpi_horas")} value={fmtNum(horas, 0, loc)} accent />
        <Kpi label={t("kpi_calorias")} value={fmtNum(kcal, 0, loc)} accent info={t("info_calorias")} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="flex min-w-0 flex-col lg:col-span-2">
          <div className="flex items-center justify-between">
            <SectionTitle>{t("sec_seguimiento")}</SectionTitle>
            <span className="hidden text-xs text-muted sm:block">
              {mode === "heatmap"
                ? t("hint_dia")
                : t(granularity === "day" ? "hint_chart_day" : "hint_chart")}
            </span>
          </div>
          <div className="mb-3 overflow-x-auto">
            <Segmented
              ariaLabel="summary-metric"
              value={mode}
              onChange={(v) => setMode(v as Mode)}
              options={[
                { value: "sesiones", label: t("m_sesiones") },
                { value: "km", label: t("m_km") },
                { value: "horas", label: t("m_horas") },
                { value: "calorias", label: t("m_cal") },
                { value: "carga", label: t("m_carga") },
                { value: "heatmap", label: t("m_heatmap") },
              ]}
            />
          </div>
          {mode === "heatmap" ? (
            <div className="flex min-w-0 flex-1 items-center">
              <WeekHeatmap acts={acts} period={period} onDay={onDay} />
            </div>
          ) : (
            <InteractiveWeeks
              rows={stacked}
              weeks={weeks}
              resetKey={weeks}
              onPeriod={setPeriod}
              granularity={granularity}
              monthStep={2}
              yDecimals={metricDecimals(mode) > 0}
              tipFormatter={(v) => {
                const n = v ?? 0;
                if (mode === "km") return `${fmtNum(n, 1, loc)} km`;
                if (mode === "horas") return `${fmtNum(n, 1, loc)} h`;
                if (mode === "calorias") return `${fmtNum(n, 0, loc)} kcal`;
                if (mode === "carga") return fmtNum(n, 0, loc);
                return fmtNum(n, 0, loc);
              }}
              bars={presentCats.map((c) => ({ key: c, color: CAT_COLORS[c], name: t("cat_" + c) }))}
            />
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <SectionTitle>{t("sec_reparto")}</SectionTitle>
            <span className="hidden text-xs text-muted sm:block">{t("hint_donut")}</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pie}
                dataKey="value"
                nameKey="name"
                innerRadius={55}
                outerRadius={95}
                paddingAngle={3}
                onClick={(d: any) => {
                  const c = d?.payload?.cat as Cat | undefined;
                  if (c) setSelectedCat((cur) => (cur === c ? null : c));
                }}
                className="cursor-pointer"
              >
                {pie.map((d) => (
                  <Cell
                    key={d.cat}
                    fill={CAT_COLORS[d.cat]}
                    opacity={selectedCat && selectedCat !== d.cat ? 0.3 : 1}
                    stroke={selectedCat === d.cat ? "#FC4C02" : "transparent"}
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip content={<DonutTooltip t={t} loc={loc} palette={palette} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <SectionTitle>{anyFilter ? t("sec_acts_periodo") : t("sec_ultimas")}</SectionTitle>
          <span className="text-xs text-muted">
            {t("txt_n_acts", { n: tableActs.length })} · {t("hint_fila")}
          </span>
        </div>
        <ActivityTable acts={tableActs} gearMap={gearMap} />
      </Card>
    </div>
  );
}
